/**
 * Выбор клиник врача — связь редактируется с обеих сторон (§3).
 *
 * Правки копятся в ЧЕРНОВИКЕ окна и применяются по кнопке «ОК», а не по
 * каждому щелчку: врач часто работает в трёх-четырёх клиниках, и промежуточные
 * состояния (снял одну, ещё не поставил другую) не должны попадать в заказы.
 *
 * Пустой список подсвечен красным намеренно: у 31 врача из 59 в исходных
 * данных лаборатории привязка не разобрана, и это надо доразметить, а не
 * считать нормой.
 */

import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import type { ApiClinic } from '../api/queries.js';
import { useAutoFocus } from './useAutoFocus.js';
import { isCoarsePointer, usePopover } from './usePopover.js';
import { подсказкаПоиска } from './isNarrow.js';

export function ClinicPicker({
  clinics,
  selected,
  onChange,
}: {
  clinics: ApiClinic[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const popover = usePopover({ width: 280, height: 340 });
  const [draft, setDraft] = useState<string[]>(selected);
  const [query, setQuery] = useState('');
  /*
   * Фокус без прокрутки: иначе таблица врачей прыгает к началу. На телефоне
   * фокуса нет вовсе — он поднимает клавиатуру, а та закрывает список.
   * Понадобится поиск — по полю можно нажать.
   */
  const searchRef = useAutoFocus<HTMLInputElement>(popover.open && !isCoarsePointer());

  const start = (element: HTMLElement) => {
    setDraft(selected);
    setQuery('');
    popover.openAt(element);
  };

  const toggle = (id: string) =>
    setDraft((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

  const confirm = () => {
    onChange(draft);
    popover.close();
  };

  const filtered = query.trim()
    ? clinics.filter((c) => c.name.toLowerCase().includes(query.trim().toLowerCase()))
    : clinics;

  return (
    <>
      <button
        onClick={(event) => (popover.open ? popover.close() : start(event.currentTarget))}
        className="flex min-h-[32px] w-full flex-wrap items-center gap-1 rounded-md border px-2 py-1 text-left text-xs"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
      >
        {/* Без клиники — не ошибка: в заказе такому врачу доступны все клиники. */}
        {selected.length === 0 && <span className="text-ink2">Без клиники</span>}
        {selected.map((id) => {
          const clinic = clinics.find((c) => c.id === id);
          if (!clinic) return null;
          return (
            <span key={id} className="rounded px-1.5 py-0.5 text-ink" style={{ background: 'var(--otc-bg)' }}>
              {clinic.name}
            </span>
          );
        })}
        <ChevronDown size={12} className="ml-auto flex-shrink-0 text-ink2" />
      </button>

      {popover.open && (
        <div
          ref={popover.popoverRef}
          className="flex flex-col rounded-lg border p-2 shadow-lg"
          style={popover.style}
        >
          <input
            ref={searchRef}
            className="mb-1 rounded border px-2 py-1 text-xs outline-none"
            style={{ borderColor: 'var(--otc-line)', background: 'transparent' }}
            placeholder={подсказкаПоиска('Поиск клиники')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {/* Перечень забирает остаток высоты: на низком окне жёсткие
              220 пикселей выталкивали кнопку «ОК» за край экрана. */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {filtered.map((clinic) => {
              const active = draft.includes(clinic.id);
              return (
                <button
                  key={clinic.id}
                  onClick={() => toggle(clinic.id)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-black/5"
                >
                  <span className="flex-1 text-ink">{clinic.name}</span>
                  {active && <Check size={14} style={{ color: 'var(--otc-accent)' }} />}
                </button>
              );
            })}
            {filtered.length === 0 && <p className="px-2 py-2 text-xs text-ink2">Ничего не найдено</p>}
          </div>

          <div className="mt-2 flex flex-shrink-0 gap-2 border-t pt-2" style={{ borderColor: 'var(--otc-line)' }}>
            <button
              onClick={confirm}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-1.5 text-sm text-white"
              style={{ background: 'var(--otc-accent)' }}
            >
              <Check size={13} /> ОК
            </button>
            <button
              onClick={popover.close}
              className="rounded-lg border px-3 py-1.5 text-sm text-ink2"
              style={{ borderColor: 'var(--otc-line)' }}
            >
              Отмена
            </button>
          </div>
        </div>
      )}
    </>
  );
}
